import type maplibregl from 'maplibre-gl'
import type { AlertZone } from './alertZones'

/**
 * Draws the subscriber's alert zones (see alertZones.ts) as translucent
 * rectangles, so the areas they'll be notified about are visible on the map
 * rather than hidden in a settings list.
 */
export class AlertZoneOverlay {
  private map: maplibregl.Map
  private sourceId = 'alert-zones-source'
  private fillLayerId = 'alert-zones-fill'
  private outlineLayerId = 'alert-zones-outline'

  constructor(map: maplibregl.Map) {
    this.map = map
    this.initLayers()
  }

  private initLayers() {
    this.map.addSource(this.sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    this.map.addLayer({
      id: this.fillLayerId,
      type: 'fill',
      source: this.sourceId,
      paint: {
        'fill-color': '#f59e0b', // amber, matching the enabled bell
        'fill-opacity': 0.12,
      },
    })

    this.map.addLayer({
      id: this.outlineLayerId,
      type: 'line',
      source: this.sourceId,
      paint: {
        'line-color': '#f59e0b',
        'line-width': 2,
        'line-dasharray': [2, 1],
      },
    })
  }

  setZones(zones: AlertZone[]) {
    const source = this.map.getSource<maplibregl.GeoJSONSource>(this.sourceId)
    if (!source) return

    source.setData({
      type: 'FeatureCollection',
      features: zones.map((zone) => ({
        type: 'Feature' as const,
        properties: { id: zone.id },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [
            [
              [zone.west, zone.south],
              [zone.east, zone.south],
              [zone.east, zone.north],
              [zone.west, zone.north],
              [zone.west, zone.south],
            ],
          ],
        },
      })),
    })
  }

  destroy() {
    if (this.map.getLayer(this.outlineLayerId)) {
      this.map.removeLayer(this.outlineLayerId)
    }
    if (this.map.getLayer(this.fillLayerId)) {
      this.map.removeLayer(this.fillLayerId)
    }
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId)
    }
  }
}
